import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import HandoutImage from '@/images/resources/handout.png'
import ComplianceImage from '@/images/resources/compliance.png'
import RiskAssessmentImage from '@/images/resources/riskAssessment.png'
import DataSheetImage from '@/images/resources/datasheet.png'
import TrainingImage from '@/images/resources/training.png'
import LegalImage from '@/images/resources/legal.png'
import { logEvent } from '@/lib/gtm'
import { SELF_ASSESSMENT_LOCALES } from '@/lib/localization/constants'

import {
  HANDOUT_FORM,
  COMPLIANCE_FORM,
  RISK_ASSESSMENT,
  DATASHEET_FORM,
  TRAINING_FORM,
  LEGAL_FORM,
} from '@/routes/resources'

import { ResourceListItem } from '../types'

type Datum = ResourceListItem & {
  locales?: string[]
}

const EBOOKS: Datum[] = [
  {
    title: 'list.handout.title',
    description: 'list.handout.description',
    href: HANDOUT_FORM.pathname,
    image: HandoutImage,
    buttonLabel: 'list.viewDetail',
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'PDPA Resources',
          action: 'Click CTA',
        },
      })
    },
  },
  {
    title: 'list.complianceGuildLine.title',
    description: 'list.complianceGuildLine.description',
    href: COMPLIANCE_FORM.pathname,
    image: ComplianceImage,
    buttonLabel: 'list.viewDetail',
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'Resources',
          action: 'Click CTA',
          label: 'guideline',
        },
      })
    },
  },
]

export const useEbooks = (): ResourceListItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('resource')

  return useMemo(() => {
    return EBOOKS.map((datum) => {
      if (
        typeof datum.locales !== 'undefined' &&
        !datum.locales.includes(language)
      ) {
        return null
      }

      return {
        title: t(datum.title),
        description: t(datum.description),
        buttonLabel: t(datum.buttonLabel),
        href: datum.href,
        image: datum.image,
        onClick: datum.onClick,
      }
    }).filter(Boolean) as ResourceListItem[]
  }, [t])
}

const TOOLS: Datum[] = [
  {
    title: 'list.riskAssessment.title',
    description: 'list.riskAssessment.description',
    href: RISK_ASSESSMENT.pathname,
    image: RiskAssessmentImage,
    buttonLabel: 'list.viewDetail',
    locales: SELF_ASSESSMENT_LOCALES,
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'PDPA Resources',
          action: 'Click CTA',
        },
      })
    },
  },
]

export const useTools = (): ResourceListItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('resource')

  return useMemo(() => {
    return TOOLS.map((datum) => {
      if (
        typeof datum.locales !== 'undefined' &&
        !datum.locales.includes(language)
      ) {
        return null
      }

      return {
        title: t(datum.title),
        description: t(datum.description),
        buttonLabel: t(datum.buttonLabel),
        href: datum.href,
        image: datum.image,
        onClick: datum.onClick,
      }
    }).filter(Boolean) as ResourceListItem[]
  }, [t, language])
}

const DATASHEETS: Datum[] = [
  {
    title: 'list.datasheet.title',
    description: 'list.datasheet.description',
    href: DATASHEET_FORM.pathname,
    image: DataSheetImage,
    buttonLabel: 'list.viewDetail',
    locales: SELF_ASSESSMENT_LOCALES,
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'PDPA Resources',
          action: 'Click CTA',
        },
      })
    },
  },
  {
    title: 'list.trainingCourseOutline.title',
    description: 'list.trainingCourseOutline.description',
    href: TRAINING_FORM.pathname,
    image: TrainingImage,
    buttonLabel: 'list.viewDetail',
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'Resources',
          action: 'Click CTA',
          label: 'trainingOutline',
        },
      })
    },
  },
  {
    title: 'list.legal.title',
    description: 'list.legal.description',
    href: LEGAL_FORM.pathname,
    image: LegalImage,
    buttonLabel: 'list.viewDetail',
    onClick: (): void => {
      logEvent({
        ga: {
          category: 'Resources',
          action: 'Click CTA',
          label: 'legal',
        },
      })
    },
  },
]

export const useDatasheets = (): ResourceListItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('resource')

  return useMemo(() => {
    return DATASHEETS.map((datum) => {
      if (
        typeof datum.locales !== 'undefined' &&
        !datum.locales.includes(language)
      ) {
        return null
      }

      return {
        title: t(datum.title),
        description: t(datum.description),
        buttonLabel: t(datum.buttonLabel),
        href: datum.href,
        image: datum.image,
        onClick: datum.onClick,
      }
    }).filter(Boolean) as ResourceListItem[]
  }, [t, language])
}
